import { NETWORK } from '@shared/utils/constant'
import { authService } from '@/app/Services'
import type {
  WalletContext,
  WalletIdentity,
  AuthSessionParams
} from '@/types/auth'

export const getWalletContext = (): null | WalletContext => {
  const sharedWalletStore = useSharedWalletStore()
  const autoSign = sharedWalletStore.autoSign

  if (!sharedWalletStore.isUserConnected || !autoSign) {
    return null
  }

  return {
    autoSign,
    identity: {
      signer: autoSign.injectiveAddress,
      granter: sharedWalletStore.injectiveAddress
    }
  }
}

export const identityKey = ({ granter, signer }: WalletIdentity) =>
  `${granter}:${signer}`

export const identityMatches = (identity: WalletIdentity) => {
  const current = getWalletContext()

  return (
    !!current &&
    current.identity.signer === identity.signer &&
    current.identity.granter === identity.granter
  )
}

export const performLogin = async ({
  identity,
  autoSign
}: WalletContext): Promise<null | AuthSessionParams> => {
  const { withAutoSignPrivateKey } = await import('@shared/wallet/autosign')

  return await withAutoSignPrivateKey(autoSign, async (privateKey) => {
    const { data, signature } = await authService.signNonce({
      privateKey,
      signer: identity.signer
    })

    const authResponse = await authService.authorize({
      data,
      signature,
      network: NETWORK,
      sender: identity.signer,
      granterAddress: identity.granter
    })

    if (!identityMatches(identity)) {
      return null
    }

    return {
      signerAddress: identity.signer,
      granterAddress: identity.granter,
      expiresAt: authResponse.expiresAt,
      accessToken: authResponse.accessToken,
      refreshTokenExpiresAt: authResponse.refreshTokenExpiresAt
    }
  })
}
